import React, { FC, useCallback, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useStore } from '@/components/store/Store';
import { PageStatusBatchProgressResponse, PageStatusResponse } from '@/declarations/models/PageStatusResponse';
import RainbowLinearProgress from '@/components/RainbowLinearProgress';
import { checkAllFrontPageStatusesInBatches, checkSitePageStatusesInBatches } from './PageStatusBatchHelperFunctions';
import {
  FRONT_PAGE_STATUS_BATCH_SIZE,
  SITE_PAGE_STATUS_BATCH_SIZE,
  RESPONSE_TIME_WARNING_LIMIT_MS,
} from './pageStatusConstants';
import { PageStatusResult } from './pageStatusTypes';
import {
  createEmptyPageStatusResponse,
  createEmptyStatusProgress,
  getDisplayUrl,
  getEffectiveUrlDisplay,
  getNumericResponseTime,
  isAttentionStatus,
} from './pageStatusUtils';
import { PageStatusDashboard } from './components/PageStatusDashboard';
import { PageStatusDetailedResultsTable } from './components/PageStatusDetailedResultsTable';

const escapeCsvValue = (value: string | number | boolean | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const createExportFileName = (testLabel: string): string => {
  const timestamp = new Date()
    .toISOString()
    .replace(/\.\d{3}Z$/, '')
    .replace(/[:T]/g, '-');

  return `page-status-${testLabel}-${timestamp}.csv`;
};

const downloadTextFile = (content: string, fileName: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
};

const createPageStatusCsv = (
  pageStatus: PageStatusResponse,
  testLabel: string,
  progress: PageStatusBatchProgressResponse | null,
): string => {
  const exportedAt = new Date().toISOString();
  const checkedCount = progress?.checked ?? pageStatus.results.length;
  const totalCount = progress?.total || pageStatus.summary.total_urls;

  const headers = [
    'exported_at',
    'test_type',
    'checked',
    'total',
    'success_count',
    'count_300',
    'count_400',
    'count_500',
    'count_na',
    'average_response_time_ms',
    'median_response_time_ms',
    'p90_response_time_ms',
    'p95_response_time_ms',
    'elapsed_time_seconds',
    'status_code',
    'url',
    'effective_url',
    'response_time_ms',
    'needs_attention',
    `slow_over_${RESPONSE_TIME_WARNING_LIMIT_MS}_ms`,
    'error',
  ];

  const rows = (pageStatus.results as PageStatusResult[]).map((result) => {
    const responseTime = getNumericResponseTime(result.response_time);
    const effectiveUrl = getEffectiveUrlDisplay(result);

    return [
      exportedAt,
      testLabel,
      checkedCount,
      totalCount,
      pageStatus.summary.success_count,
      pageStatus.summary.count_300,
      pageStatus.summary.count_400,
      pageStatus.summary.count_500,
      pageStatus.summary.count_na,
      pageStatus.summary.times.average_response_time_ms,
      pageStatus.summary.times.median_response_time_ms,
      pageStatus.summary.times.p90_response_time_ms,
      pageStatus.summary.times.p95_response_time_ms,
      pageStatus.summary.times.elapsed_time_seconds,
      result.status_code ?? 'N/A',
      getDisplayUrl(result),
      effectiveUrl,
      responseTime ?? 'N/A',
      isAttentionStatus(result.status_code),
      responseTime !== null && responseTime >= RESPONSE_TIME_WARNING_LIMIT_MS,
      result.error ?? '',
    ];
  });

  return [headers, ...rows].map((row) => row.map(escapeCsvValue).join(',')).join('\n');
};

export const PageStatusTestView: FC = () => {
  const [hasFetched, setHasFetched] = useState(false);
  const { state } = useStore();
  const siteId = state.selectedSite?.id;
  const [loading, setLoading] = useState(false);
  const [pageStatus, setPageStatus] = useState<PageStatusResponse | null>(null);
  const [statusProgress, setStatusProgress] = useState<PageStatusBatchProgressResponse | null>(null);
  const [activeTestLabel, setActiveTestLabel] = useState('sider');

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleExportResults = useCallback(() => {
    if (!pageStatus || pageStatus.results.length === 0) {
      return;
    }

    const csv = createPageStatusCsv(pageStatus, activeTestLabel, statusProgress);
    const fileName = createExportFileName(activeTestLabel);

    downloadTextFile(csv, fileName, 'text/csv;charset=utf-8');
  }, [activeTestLabel, pageStatus, statusProgress]);

  const handleStartFrontPagesStatusTest = useCallback(async () => {
    setLoading(true);
    setHasFetched(true);
    setActiveTestLabel('forsider');
    setPageStatus(createEmptyPageStatusResponse());
    setStatusProgress(createEmptyStatusProgress(FRONT_PAGE_STATUS_BATCH_SIZE));

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const finalResponse = await checkAllFrontPageStatusesInBatches({
        batchSize: FRONT_PAGE_STATUS_BATCH_SIZE,
        intervalMs: 250,
        errorsOnly: false,
        signal: abortController.signal,
        onProgress: (pageStatusResponse, progressResponse) => {
          setPageStatus(pageStatusResponse);
          setStatusProgress(progressResponse);
        },
      });

      setPageStatus(finalResponse);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      console.error('Error during front page test:', error);
      setPageStatus(null);
      setStatusProgress(null);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, []);

  const handleCancelStatusTest = useCallback(() => {
    abortControllerRef.current?.abort();
    setLoading(false);
  }, []);

  const handleStartSitePagesStatusTest = useCallback(async () => {
    if (siteId === undefined) {
      return;
    }

    setLoading(true);
    setHasFetched(true);
    setActiveTestLabel('sider');
    setPageStatus(createEmptyPageStatusResponse());
    setStatusProgress(createEmptyStatusProgress(SITE_PAGE_STATUS_BATCH_SIZE));

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const finalResponse = await checkSitePageStatusesInBatches({
        siteId,
        batchSize: SITE_PAGE_STATUS_BATCH_SIZE,
        intervalMs: 250,
        errorsOnly: false,
        signal: abortController.signal,
        onProgress: (pageStatusResponse, progressResponse) => {
          setPageStatus(pageStatusResponse);
          setStatusProgress(progressResponse);
        },
      });

      setPageStatus(finalResponse);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      console.error('Error during site pages test:', error);
      setPageStatus(null);
      setStatusProgress(null);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [siteId]);

  const progressPercent = statusProgress?.progress_percent ?? 0;
  const shouldShowProgress = hasFetched && loading && statusProgress !== null && progressPercent < 100;
  const canExportResults = pageStatus !== null && pageStatus.results.length > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        mt: 1,
      }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        useFlexGap
        sx={{
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          width: '100%',
          maxWidth: '100%',
        }}>
        <Button
          variant='outlined'
          onClick={handleStartSitePagesStatusTest}
          disabled={loading || siteId === undefined}
          sx={{ width: { xs: '100%', sm: 'auto' } }}>
          Test alle sider på nåværende nettsted
        </Button>

        <Button
          variant='outlined'
          onClick={handleStartFrontPagesStatusTest}
          disabled={loading}
          sx={{ width: { xs: '100%', sm: 'auto' } }}>
          Test alle nettsteder (forsider)
        </Button>

        <Button
          variant='outlined'
          onClick={handleExportResults}
          disabled={!canExportResults}
          sx={{ width: { xs: '100%', sm: 'auto' } }}>
          Eksporter resultater
        </Button>

        {loading && statusProgress && (
          <Button variant='outlined' onClick={handleCancelStatusTest} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Avbryt
          </Button>
        )}
      </Stack>

      {shouldShowProgress && (
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1400 }, mt: 3, minWidth: 0 }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            Tester {activeTestLabel}: {statusProgress?.checked ?? 0} av {statusProgress?.total ?? 0} ({progressPercent}
            %)
          </Typography>

          <RainbowLinearProgress variant='determinate' value={progressPercent} sx={{ width: '100%' }} />
        </Box>
      )}

      {hasFetched && pageStatus && (
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1400 }, minWidth: 0, mt: 3 }}>
          <PageStatusDashboard pageStatus={pageStatus} isRunning={loading} progress={statusProgress} />

          <PageStatusDetailedResultsTable
            title='Alle resultater'
            helper='Alle testede sider, sortert med feil først og deretter tregeste responstid.'
            results={pageStatus.results as PageStatusResult[]}
          />
        </Box>
      )}
    </Box>
  );
};
